/**
 * Reusable form input components with theme support (light/dark)
 * Provides consistent styling and accessibility for all form fields
 */

import { forwardRef, useState } from 'react'
import { Icon } from './Icon'

// Shared input styles - theme aware
const inputBaseStyles = `
  w-full px-4 py-3 rounded-xl
  bg-white dark:bg-dial-surface border
  text-gray-900 dark:text-indices-bright placeholder:text-gray-400 dark:placeholder:text-indices-muted
  transition-all duration-200
  disabled:bg-gray-100 dark:disabled:bg-dial-black disabled:text-gray-400 dark:disabled:text-indices-muted disabled:cursor-not-allowed disabled:border-gray-200 dark:disabled:border-dial-border
`

const inputNormalStyles = 'border-gray-300 dark:border-dial-border focus:border-primary-500 dark:focus:border-lume-teal focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-lume-teal/20'
const inputErrorStyles = 'border-status-error focus:border-status-error focus:ring-2 focus:ring-status-error/20'

/**
 * Base input wrapper with label and error handling
 */
export function FormField({ label, error, hint, required, children, className = '' }) {
  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-indices-medium mb-1.5 leading-normal">
          {label}
          {required && <span className="text-status-error ml-0.5">*</span>}
        </label>
      )}
      {children}
      {hint && !error && (
        <p className="mt-1.5 text-xs text-gray-500 dark:text-indices-muted leading-relaxed">{hint}</p>
      )}
      {error && (
        <p className="mt-1.5 text-xs text-status-error leading-relaxed" role="alert">{error}</p>
      )}
    </div>
  )
}

/**
 * Text input component
 */
export const TextInput = forwardRef(function TextInput(
  {
    label,
    error,
    hint,
    required,
    icon,
    iconPosition = 'left',
    className = '',
    inputClassName = '',
    ...props
  },
  ref
) {
  const iconPaddingLeft = icon && iconPosition === 'left' ? 'pl-11' : ''
  const iconPaddingRight = icon && iconPosition === 'right' ? 'pr-11' : ''

  const input = (
    <div className="relative">
      {icon && iconPosition === 'left' && (
        <div className="absolute top-0 bottom-0 left-0 pl-3.5 flex items-center pointer-events-none">
          <span className="text-gray-400 dark:text-indices-muted">{icon}</span>
        </div>
      )}
      <input
        ref={ref}
        className={`
          ${inputBaseStyles}
          ${error ? inputErrorStyles : inputNormalStyles}
          ${iconPaddingLeft} ${iconPaddingRight}
          ${inputClassName}
        `}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? `${props.id}-error` : undefined}
        {...props}
      />
      {icon && iconPosition === 'right' && (
        <div className="absolute top-0 bottom-0 right-0 pr-3.5 flex items-center pointer-events-none">
          <span className="text-gray-400 dark:text-indices-muted">{icon}</span>
        </div>
      )}
    </div>
  )

  if (!label && !error && !hint) {
    return input
  }

  return (
    <FormField label={label} error={error} hint={hint} required={required} className={className}>
      {input}
    </FormField>
  )
})

/**
 * Password input with show/hide toggle
 */
export const PasswordInput = forwardRef(function PasswordInput(
  { label, error, hint, required, className = '', ...props },
  ref
) {
  const [showPassword, setShowPassword] = useState(false)

  return (
    <FormField label={label} error={error} hint={hint} required={required} className={className}>
      <div className="relative">
        <input
          ref={ref}
          type={showPassword ? 'text' : 'password'}
          className={`
            ${inputBaseStyles}
            pr-12
            ${error ? inputErrorStyles : inputNormalStyles}
          `}
          aria-invalid={error ? 'true' : 'false'}
          {...props}
        />
        <button
          type="button"
          className="absolute top-0 bottom-0 right-0 pr-3.5 flex items-center text-gray-400 dark:text-indices-muted hover:text-gray-600 dark:hover:text-indices-bright transition-colors duration-150 focus:outline-none focus-visible:text-primary-500 dark:focus-visible:text-lume-teal"
          onClick={() => setShowPassword(!showPassword)}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          tabIndex={-1}
        >
          <Icon name={showPassword ? 'eyeOff' : 'eye'} className="w-5 h-5" />
        </button>
      </div>
    </FormField>
  )
})

/**
 * Textarea component
 */
export const TextArea = forwardRef(function TextArea(
  { label, error, hint, required, className = '', rows = 4, ...props },
  ref
) {
  return (
    <FormField label={label} error={error} hint={hint} required={required} className={className}>
      <textarea
        ref={ref}
        rows={rows}
        className={`
          ${inputBaseStyles}
          resize-none
          ${error ? inputErrorStyles : inputNormalStyles}
        `}
        aria-invalid={error ? 'true' : 'false'}
        {...props}
      />
    </FormField>
  )
})

/**
 * Select dropdown component
 */
export const Select = forwardRef(function Select(
  { label, error, hint, required, options = [], placeholder, className = '', ...props },
  ref
) {
  return (
    <FormField label={label} error={error} hint={hint} required={required} className={className}>
      <div className="relative">
        <select
          ref={ref}
          className={`
            ${inputBaseStyles}
            appearance-none bg-white dark:bg-dial-surface pr-10 cursor-pointer
            ${error ? inputErrorStyles : inputNormalStyles}
          `}
          aria-invalid={error ? 'true' : 'false'}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className="bg-white dark:bg-dial-surface text-gray-900 dark:text-indices-bright"
            >
              {option.label}
            </option>
          ))}
        </select>
        <div className="absolute top-0 bottom-0 right-0 pr-3 flex items-center pointer-events-none">
          <Icon name="chevronDown" className="w-5 h-5 text-gray-400 dark:text-indices-muted" />
        </div>
      </div>
    </FormField>
  )
})

/**
 * Checkbox component with larger touch target
 */
export const Checkbox = forwardRef(function Checkbox(
  { label, error, hint, className = '', ...props },
  ref
) {
  return (
    <div className={className}>
      <label className="flex items-start gap-3 cursor-pointer group">
        <div className="flex items-center justify-center w-6 h-6 mt-0.5">
          <input
            ref={ref}
            type="checkbox"
            className="
              w-5 h-5 rounded border-gray-300 dark:border-dial-border bg-white dark:bg-dial-surface
              text-primary-600 dark:text-lume-teal
              focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-lume-teal/20 focus:ring-offset-0 focus:ring-offset-white dark:focus:ring-offset-dial-black
              transition-colors duration-150
              disabled:opacity-50 disabled:cursor-not-allowed
              cursor-pointer
            "
            {...props}
          />
        </div>
        {label && (
          <span className="text-sm text-gray-600 dark:text-indices-medium leading-relaxed group-hover:text-gray-900 dark:group-hover:text-indices-bright transition-colors">
            {label}
          </span>
        )}
      </label>
      {hint && !error && (
        <p className="mt-1 ml-9 text-xs text-gray-500 dark:text-indices-muted">{hint}</p>
      )}
      {error && (
        <p className="mt-1 ml-9 text-xs text-status-error" role="alert">{error}</p>
      )}
    </div>
  )
})

/**
 * Radio button group component with larger touch targets
 */
export function RadioGroup({
  label,
  error,
  hint,
  required,
  options = [],
  value,
  onChange,
  name,
  className = '',
  direction = 'vertical',
}) {
  return (
    <FormField label={label} error={error} hint={hint} required={required} className={className}>
      <div
        className={`${direction === 'horizontal' ? 'flex flex-wrap gap-4' : 'space-y-3'}`}
        role="radiogroup"
        aria-label={label}
      >
        {options.map((option) => (
          <label
            key={option.value}
            className="flex items-center gap-3 cursor-pointer group"
          >
            <div className="flex items-center justify-center w-6 h-6">
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={value === option.value}
                onChange={(e) => onChange?.(e.target.value)}
                disabled={option.disabled}
                className="
                  w-5 h-5 border-gray-300 dark:border-dial-border bg-white dark:bg-dial-surface
                  text-primary-600 dark:text-lume-teal
                  focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-lume-teal/20 focus:ring-offset-0 focus:ring-offset-white dark:focus:ring-offset-dial-black
                  transition-colors duration-150
                  disabled:opacity-50 disabled:cursor-not-allowed
                  cursor-pointer
                "
              />
            </div>
            <span className="text-sm text-gray-600 dark:text-indices-medium group-hover:text-gray-900 dark:group-hover:text-indices-bright transition-colors">
              {option.label}
            </span>
          </label>
        ))}
      </div>
    </FormField>
  )
}

/**
 * Search input with icon
 */
export const SearchInput = forwardRef(function SearchInput(
  { placeholder = 'Search...', onClear, className = '', ...props },
  ref
) {
  return (
    <div className={`relative ${className}`}>
      <div className="absolute top-0 bottom-0 left-0 pl-3.5 flex items-center pointer-events-none">
        <Icon name="search" className="w-5 h-5 text-gray-400 dark:text-indices-muted" />
      </div>
      <input
        ref={ref}
        type="search"
        placeholder={placeholder}
        className={`
          ${inputBaseStyles}
          ${inputNormalStyles}
          pl-11 pr-4
        `}
        {...props}
      />
      {props.value && onClear && (
        <button
          type="button"
          onClick={onClear}
          className="
            absolute top-0 bottom-0 right-0 pr-3.5 flex items-center
            text-gray-400 dark:text-indices-muted hover:text-gray-600 dark:hover:text-indices-bright
            transition-colors duration-150
          "
          aria-label="Clear search"
        >
          <Icon name="close" className="w-5 h-5" />
        </button>
      )}
    </div>
  )
})

export default {
  FormField,
  TextInput,
  PasswordInput,
  TextArea,
  Select,
  Checkbox,
  RadioGroup,
  SearchInput,
}
